import React, { useEffect, useState } from "react";
import MainLayout from "layouts/Main";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Forecast from "./Forecast";
import PriceLimits from "./PriceLimits";
import BaseLoads from "./BaseLoads";
import ScheduledSupplyPlans from "./ScheduledSupplyPlans";
import dayjs from "dayjs";
import { ProtectedPaths } from "routes";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  selectClient,
  selectCurrentRoleClientId,
  selectMe,
} from "store/selectors";
import Select from "components/Select";

import {
  getNPAuctionDeadlines,
  auctionIds,
  auctionCodes,
  getNextNPAuction,
  hasNPAuctionDeadlinePassed,
} from "helpers/auctions";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { fetchClient } from "store/appSlice";

const SupplyPlans = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const { date } = useParams();
  const dispatch = useAppDispatch();
  const currentRoleClientId = useAppSelector(selectCurrentRoleClientId);
  const me = useAppSelector(selectMe);
  const client = useAppSelector(selectClient);

  useEffect(() => {
    if (currentRoleClientId) {
      dispatch(fetchClient(currentRoleClientId));
    }
  }, [currentRoleClientId]);

  useEffect(() => {
    if (me?.roles) {
      const isUserAdmin = me.roles.some((role) => role.name === "ROLE_ADMIN");
      setIsAdmin(isUserAdmin);
    }
  }, [me]);

  useEffect(() => {
    if (client && client.id) {
      if (
        client.contractType !== "FIXED" &&
        client.contractType !== "EXTERNAL"
      ) {
        navigate(`/`);
      }
      if (client.contractType === "EXTERNAL" && selectedTab !== 0) {
        setSelectedTab(0);
      }
    }
  }, [client]);

  const [selectedTab, setSelectedTab] = useState(
    Number(searchParams.get("tab")) ?? 0,
  );
  const [dateFilter, setDateFilter] = useState(date ? dayjs(date) : null);
  const [snackbar, setSnackbar] = useState(null);
  const [selectedAuction, setSelectedAuction] = useState<number | "">(
    Number(searchParams.get("auction")) ?? "",
  );

  const auctionDeadlines = getNPAuctionDeadlines(dateFilter);

  useEffect(() => {
    const nextAuction =
      selectedAuction &&
      !hasNPAuctionDeadlinePassed(selectedAuction, dateFilter)
        ? auctionCodes[selectedAuction as keyof typeof auctionCodes]
        : getNextNPAuction(dateFilter);

    setSelectedAuction(auctionIds[nextAuction as keyof typeof auctionIds]);

    if (dateFilter) {
      navigate(
        `/${ProtectedPaths.SupplyPlans}/${dayjs(dateFilter).format("YYYY-MM-DD")}/orders?auction=${auctionIds[nextAuction as keyof typeof auctionIds]}&tab=${selectedTab}`,
        { replace: true },
      );
    }
  }, [navigate, selectedTab, dateFilter]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    navigate(
      `/${ProtectedPaths.SupplyPlans}/${dayjs(dateFilter).format("YYYY-MM-DD")}/orders?auction=${selectedAuction}&tab=${newValue}`,
      { replace: true },
    );
  };

  const handleDateChange = (newValue: any) => {
    const formattedDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
    setDateFilter(dayjs(formattedDate));

    navigate(
      `/${ProtectedPaths.SupplyPlans}/${dayjs(newValue).format("YYYY-MM-DD")}/orders?auction=${0}&tab=${0}`,
      { replace: true },
    );
  };

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  if (
    client &&
    (client.contractType === "FIXED" || client.contractType === "EXTERNAL")
  ) {
    return (
      <MainLayout
        tabs={[
          {
            label: "Orders",
            link: dateFilter
              ? `supply-plans/${dayjs(dateFilter).format("YYYY-MM-DD")}/orders`
              : `supply-plans/orders`,
            selected: true,
          },
          ...(client.contractType === "FIXED"
            ? [
                {
                  label: "Results",
                  link: dateFilter
                    ? `supply-plans/${dayjs(dateFilter).format("YYYY-MM-DD")}/results`
                    : `supply-plans/results`,
                },
              ]
            : []),
        ]}
        snackbar={{
          open: snackbar,
          handleClose: snackbarClose,
          type: snackbar?.type,
          message: snackbar?.message,
        }}
      >
        <Box sx={{ mt: "1.2rem" }}>
          <DatePicker
            label={"Select date"}
            sx={{ background: "white", mr: "1rem" }}
            slotProps={{
              textField: { size: "small" },
              clearButton: { size: "small" },
            }}
            value={dateFilter || null}
            onChange={(newValue) => {
              handleDateChange(newValue);
            }}
          />
          <Select
            label="Auction"
            id="auction"
            name="auction"
            minWidth={150}
            maxWidth={150}
            value={selectedAuction}
            onChange={(e: any) => {
              setSelectedAuction(e.target.value);
              navigate(
                `/${ProtectedPaths.SupplyPlans}/${dayjs(dateFilter).format("YYYY-MM-DD")}/orders?auction=${e.target.value}&tab=${selectedTab}`,
                { replace: true },
              );
            }}
            options={[
              {
                label: "Day-ahead",
                value: 1,
                optionDisabled:
                  !isAdmin && dayjs().isAfter(auctionDeadlines.DayAhead),
              },
              {
                label: "IDA1",
                value: 2,
                optionDisabled:
                  !isAdmin &&
                  dayjs().isAfter(auctionDeadlines.SIDC_IntradayAuction1),
              },
              {
                label: "IDA2",
                value: 3,
                optionDisabled:
                  !isAdmin &&
                  dayjs().isAfter(auctionDeadlines.SIDC_IntradayAuction2),
              },
              {
                label: "IDA3",
                value: 4,
                optionDisabled:
                  !isAdmin &&
                  dayjs().isAfter(auctionDeadlines.SIDC_IntradayAuction3),
              },
            ]}
          />
        </Box>
        {dateFilter && (
          <Stack direction="row" spacing={2} sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h5">Orders</Typography>
            {selectedAuction && (
              <Stack direction="row" spacing={1}>
                <Chip
                  label={
                    auctionCodes[selectedAuction as keyof typeof auctionCodes]
                  }
                />
                <Tooltip title="Deadline for order">
                  <Chip
                    icon={<ScheduleIcon />}
                    label={`${dayjs(auctionDeadlines[auctionCodes[selectedAuction as keyof typeof auctionCodes] as keyof typeof auctionDeadlines]).format("DD.MM.YYYY HH:mm")}`}
                    variant="outlined"
                  />
                </Tooltip>
              </Stack>
            )}
          </Stack>
        )}
        {dateFilter && !selectedAuction && (
          <Box sx={{ mt: 4 }}>
            <Typography component="p">
              All auctions over for selected date. Please see results.
            </Typography>
          </Box>
        )}
        {dateFilter && selectedAuction && (
          <Box>
            <Box sx={{ mt: 4, mb: 4, borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={selectedTab} onChange={handleTabChange}>
                {client.contractType === "FIXED" && <Tab label="Forecast" />}
                {(client.contractType === "FIXED" ||
                  client.contractType === "EXTERNAL") && (
                  <Tab label="Price limits" />
                )}
                {client.contractType === "FIXED" && <Tab label="Base loads" />}
                {client.contractType === "FIXED" && (
                  <Tab label="Scheduled supply plan" />
                )}
              </Tabs>
            </Box>
            {selectedTab === 0 && client.contractType !== "EXTERNAL" && (
              <Box>
                <Forecast
                  selectedAuction={selectedAuction}
                  currentRoleClientId={currentRoleClientId}
                  date={dateFilter}
                  setSnackbar={setSnackbar}
                />
              </Box>
            )}
            {(selectedTab === 1 || client.contractType === "EXTERNAL") && (
              <Box>
                <PriceLimits
                  setSnackbar={setSnackbar}
                  selectedAuction={selectedAuction}
                  currentRoleClientId={currentRoleClientId}
                />
              </Box>
            )}
            {selectedTab === 2 && (
              <Box>
                <BaseLoads setSnackbar={setSnackbar} />
              </Box>
            )}
            {selectedTab === 3 && (
              <Box>
                <ScheduledSupplyPlans
                  date={dateFilter}
                  currentRoleClientId={currentRoleClientId}
                  selectedAuction={selectedAuction}
                  setSnackbar={setSnackbar}
                />
              </Box>
            )}
          </Box>
        )}
      </MainLayout>
    );
  }
  return null;
};

export default SupplyPlans;
